import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { motion, AnimatePresence } from 'framer-motion';
import Header from '../../components/ui/Header';

import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import TrainerExerciseLibraryPanel from './components/TrainerExerciseLibraryPanel';
import WeeklyProgramTimeline from './components/WeeklyProgramTimeline';
import PlanConfigPanel from './components/PlanConfigPanel';
import NutritionFoodLibraryPanel from './components/NutritionFoodLibraryPanel';
import DailyMealPlannerTimeline from './components/DailyMealPlannerTimeline';
import NutritionPlanConfigPanel from './components/NutritionPlanConfigPanel';
import WorkoutBuilderTour from './components/WorkoutBuilderTour';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const MEALS = ['Breakfast', 'Lunch', 'Dinner', 'Snack 1', 'Snack 2'];

const initWeekDays = () => DAYS?.map(name => ({ name, exercises: [], isRest: name === 'Sunday' }));
const initMealDays = () => DAYS?.map(name => ({ name, meals: MEALS?.map(m => ({ name: m, foods: [] })) }));

// Sample food library data
const SAMPLE_FOODS = [
  { id: 'f1', name: 'Chicken Breast', servingSize: '100g', calories: 165, protein: 31, carbs: 0, fats: 3.6, category: 'protein' },
  { id: 'f2', name: 'Brown Rice', servingSize: '100g', calories: 216, protein: 5, carbs: 45, fats: 1.8, category: 'grains' },
  { id: 'f3', name: 'Broccoli', servingSize: '100g', calories: 34, protein: 2.8, carbs: 7, fats: 0.4, category: 'vegetables' },
  { id: 'f4', name: 'Whole Eggs', servingSize: '1 large', calories: 72, protein: 6, carbs: 0.4, fats: 5, category: 'protein' },
  { id: 'f5', name: 'Oats', servingSize: '100g', calories: 389, protein: 17, carbs: 66, fats: 7, category: 'grains' },
  { id: 'f6', name: 'Salmon', servingSize: '100g', calories: 208, protein: 20, carbs: 0, fats: 13, category: 'protein' },
  { id: 'f7', name: 'Sweet Potato', servingSize: '100g', calories: 86, protein: 1.6, carbs: 20, fats: 0.1, category: 'carbs' },
  { id: 'f8', name: 'Greek Yogurt', servingSize: '150g', calories: 100, protein: 17, carbs: 6, fats: 0.7, category: 'dairy' },
  { id: 'f9', name: 'Almonds', servingSize: '30g', calories: 174, protein: 6, carbs: 6, fats: 15, category: 'fats' },
  { id: 'f10', name: 'Banana', servingSize: '1 medium', calories: 89, protein: 1.1, carbs: 23, fats: 0.3, category: 'fruits' },
  { id: 'f11', name: 'Cottage Cheese', servingSize: '100g', calories: 98, protein: 11, carbs: 3.4, fats: 4.3, category: 'dairy' },
  { id: 'f12', name: 'Avocado', servingSize: '100g', calories: 160, protein: 2, carbs: 9, fats: 15, category: 'fats' },
  { id: 'f13', name: 'Tuna (canned)', servingSize: '100g', calories: 116, protein: 26, carbs: 0, fats: 1, category: 'protein' },
  { id: 'f14', name: 'Quinoa', servingSize: '100g', calories: 222, protein: 8, carbs: 39, fats: 4, category: 'grains' },
  { id: 'f15', name: 'Spinach', servingSize: '100g', calories: 23, protein: 2.9, carbs: 3.6, fats: 0.4, category: 'vegetables' },
  { id: 'f16', name: 'Whey Protein', servingSize: '1 scoop (30g)', calories: 120, protein: 24, carbs: 3, fats: 2, category: 'protein' },
  { id: 'f17', name: 'Olive Oil', servingSize: '1 tbsp', calories: 119, protein: 0, carbs: 0, fats: 14, category: 'fats' },
  { id: 'f18', name: 'Apple', servingSize: '1 medium', calories: 95, protein: 0.5, carbs: 25, fats: 0.3, category: 'fruits' },
  { id: 'f19', name: 'Beef (lean mince)', servingSize: '100g', calories: 215, protein: 26, carbs: 0, fats: 12, category: 'protein' },
  { id: 'f20', name: 'Whole Milk', servingSize: '250ml', calories: 149, protein: 8, carbs: 12, fats: 8, category: 'dairy' },
];

const TrainerWorkoutBuilder = () => {
  const { user } = useAuth();

  // Main tab - fixed to nutrition only
  const [mainTab, setMainTab] = useState('nutrition');

  // Workout state
  const [exercises, setExercises] = useState([]);
  const [customExercises, setCustomExercises] = useState([]);
  const [clients, setClients] = useState([]);
  const [weekDays, setWeekDays] = useState(initWeekDays());
  const [planConfig, setPlanConfig] = useState({ name: '', description: '', clientId: '', startDate: '', endDate: '' });
  const [savedPlans, setSavedPlans] = useState([]);
  const [showSavedPlans, setShowSavedPlans] = useState(false);

  // Nutrition state
  const [foods] = useState(SAMPLE_FOODS);
  const [customFoods, setCustomFoods] = useState([]);
  const [mealDays, setMealDays] = useState(initMealDays());
  const [nutritionConfig, setNutritionConfig] = useState({ name: '', description: '', clientId: '', startDate: '', endDate: '', targetCalories: 0, targetProtein: 0, targetCarbs: 0, targetFats: 0 });
  const [savedNutritionPlans, setSavedNutritionPlans] = useState([]);
  const [showSavedNutritionPlans, setShowSavedNutritionPlans] = useState(false);

  // Shared state
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [activePanel, setActivePanel] = useState('library');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (user?.id) loadData();
  }, [user?.id]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [exercisesRes, clientsRes, customRes, plansRes, nutritionPlansRes] = await Promise.all([
        supabase?.from('exercises')?.select('*')?.eq('is_public', true)?.order('name'),
        supabase?.from('pt_client_assignments')?.select('*, client:user_profiles!pt_client_assignments_client_id_fkey(id, full_name, email)')?.eq('trainer_id', user?.id)?.eq('status', 'active'),
        supabase?.from('exercises')?.select('*')?.eq('created_by', user?.id)?.eq('is_public', false),
        supabase?.from('client_workout_plans')?.select('*, client:user_profiles!client_workout_plans_client_id_fkey(full_name)')?.eq('trainer_id', user?.id)?.order('created_at', { ascending: false })?.limit(10),
        supabase?.from('nutrition_plans')?.select('*, client:user_profiles!nutrition_plans_client_id_fkey(full_name)')?.eq('trainer_id', user?.id)?.order('created_at', { ascending: false })?.limit(10),
      ]);

      const mapExercise = (ex) => ({
        id: ex?.id,
        name: ex?.name,
        category: ex?.category,
        difficulty: ex?.difficulty,
        muscleGroups: ex?.muscle_groups || [],
        equipmentNeeded: ex?.equipment_needed || [],
        description: ex?.description,
        videoUrl: ex?.video_url,
      });

      setExercises((exercisesRes?.data || [])?.map(mapExercise));
      setCustomExercises((customRes?.data || [])?.map(mapExercise));
      setClients(clientsRes?.data || []);
      setSavedPlans(plansRes?.data || []);
      setSavedNutritionPlans(nutritionPlansRes?.data || []);
    } catch (err) {
      console.error('Load error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // ── Workout handlers ──────────────────────────────────────────────────────
  const handleAddExercise = useCallback((exercise) => {
    const targetDayIdx = weekDays?.findIndex(d => !d?.isRest) ?? 0;
    if (targetDayIdx === -1) return;
    const newItem = { id: `${exercise?.id}-${Date.now()}`, exerciseId: exercise?.id, name: exercise?.name, category: exercise?.category, sets: 3, reps: 10, restSeconds: 60 };
    setWeekDays(prev => prev?.map((day, idx) => idx === targetDayIdx ? { ...day, exercises: [...day?.exercises, newItem] } : day));
  }, [weekDays]);

  const handleAddCustomExercise = async (formData) => {
    try {
      const { data, error } = await supabase?.from('exercises')?.insert({ name: formData?.name, description: formData?.description || formData?.notes, category: 'strength', difficulty: 'intermediate', created_by: user?.id, is_public: false })?.select()?.single();
      if (!error && data) {
        setCustomExercises(prev => [...prev, { id: data?.id, name: data?.name, category: data?.category, difficulty: data?.difficulty, muscleGroups: [], equipmentNeeded: [], description: data?.description }]);
      }
    } catch (err) { console.error('Custom exercise error:', err); }
  };

  const handleDropToDay = (dayIndex, newOrder) => setWeekDays(prev => prev?.map((day, idx) => idx === dayIndex ? { ...day, exercises: newOrder } : day));
  const handleRemoveFromDay = (dayIndex, itemId) => setWeekDays(prev => prev?.map((day, idx) => idx === dayIndex ? { ...day, exercises: day?.exercises?.filter(e => e?.id !== itemId) } : day));
  const handleUpdateItem = (dayIndex, itemId, field, value) => setWeekDays(prev => prev?.map((day, idx) => idx === dayIndex ? { ...day, exercises: day?.exercises?.map(e => e?.id === itemId ? { ...e, [field]: value } : e) } : day));
  const handleToggleRestDay = (dayIndex) => setWeekDays(prev => prev?.map((day, idx) => idx === dayIndex ? { ...day, isRest: !day?.isRest } : day));
  const handleUpdateConfig = (field, value) => setPlanConfig(prev => ({ ...prev, [field]: value }));

  const handleSavePlan = async () => {
    setIsSaving(true); setErrorMsg('');
    try {
      const { data: plan, error: planError } = await supabase?.from('client_workout_plans')?.insert({ client_id: planConfig?.clientId, trainer_id: user?.id, name: planConfig?.name, description: planConfig?.description, start_date: planConfig?.startDate, end_date: planConfig?.endDate || null, is_active: true })?.select()?.single();
      if (planError) throw planError;
      for (let i = 0; i < weekDays?.length; i++) {
        const day = weekDays?.[i];
        if (day?.isRest || day?.exercises?.length === 0) continue;
        const { data: wPlan, error: wPlanError } = await supabase?.from('workout_plans')?.insert({ name: `${planConfig?.name} - ${day?.name}`, description: `${day?.name} workout`, difficulty: 'intermediate', duration_minutes: Math.ceil(day?.exercises?.reduce((acc, ex) => acc + (ex?.sets * ex?.reps * 3) + (ex?.restSeconds * (ex?.sets - 1)), 0) / 60), created_by: user?.id, is_template: true })?.select()?.single();
        if (wPlanError) throw wPlanError;
        const exerciseInserts = day?.exercises?.map((ex, orderIdx) => ({ workout_plan_id: wPlan?.id, exercise_id: ex?.exerciseId, order_index: orderIdx, sets: ex?.sets, reps: ex?.reps, rest_seconds: ex?.restSeconds }));
        if (exerciseInserts?.length > 0) { const { error: exError } = await supabase?.from('workout_plan_exercises')?.insert(exerciseInserts); if (exError) throw exError; }
        const startDate = new Date(planConfig?.startDate);
        const startDayOfWeek = startDate?.getDay() === 0 ? 6 : startDate?.getDay() - 1;
        const daysUntilTarget = (i - startDayOfWeek + 7) % 7;
        const scheduledDate = new Date(startDate); scheduledDate?.setDate(startDate?.getDate() + daysUntilTarget);
        await supabase?.from('client_workout_days')?.insert({ client_workout_plan_id: plan?.id, workout_plan_id: wPlan?.id, scheduled_date: scheduledDate?.toISOString()?.split('T')?.[0], day_label: day?.name });
      }
      setSuccessMsg(`Plan "${planConfig?.name}" saved and assigned successfully!`);
      setTimeout(() => setSuccessMsg(''), 4000);
      setPlanConfig({ name: '', description: '', clientId: '', startDate: '', endDate: '' });
      setWeekDays(initWeekDays());
      loadData();
    } catch (err) {
      console.error('Save plan error:', err);
      setErrorMsg(err?.message || 'Failed to save plan. Please try again.');
      setTimeout(() => setErrorMsg(''), 5000);
    } finally { setIsSaving(false); }
  };

  // ── Nutrition handlers ────────────────────────────────────────────────────
  const handleAddFood = useCallback((food) => {
    const targetDayIdx = 0;
    const targetMealIdx = mealDays?.[targetDayIdx]?.meals?.findIndex(m => m?.foods?.length === 0) ?? 0;
    const mealIdx = targetMealIdx === -1 ? 0 : targetMealIdx;
    const newItem = { id: `${food?.id}-${Date.now()}`, foodId: food?.id, name: food?.name, servingSize: food?.servingSize, calories: food?.calories, protein: food?.protein, carbs: food?.carbs, fats: food?.fats, quantity: 1 };
    setMealDays(prev => prev?.map((day, dIdx) =>
      dIdx === targetDayIdx ? { ...day, meals: day?.meals?.map((meal, mIdx) => mIdx === mealIdx ? { ...meal, foods: [...meal?.foods, newItem] } : meal) } : day
    ));
  }, [mealDays]);

  const handleAddCustomFood = (formData) => {
    const newFood = { id: `custom-${Date.now()}`, ...formData };
    setCustomFoods(prev => [...prev, newFood]);
  };

  const handleDropToMeal = (dayIndex, mealIndex, newOrder) => {
    setMealDays(prev => prev?.map((day, dIdx) =>
      dIdx === dayIndex ? { ...day, meals: day?.meals?.map((meal, mIdx) => mIdx === mealIndex ? { ...meal, foods: newOrder } : meal) } : day
    ));
  };

  const handleRemoveFromMeal = (dayIndex, mealIndex, itemId) => {
    setMealDays(prev => prev?.map((day, dIdx) =>
      dIdx === dayIndex ? { ...day, meals: day?.meals?.map((meal, mIdx) => mIdx === mealIndex ? { ...meal, foods: meal?.foods?.filter(f => f?.id !== itemId) } : meal) } : day
    ));
  };

  const handleUpdateQty = (dayIndex, mealIndex, itemId, qty) => {
    setMealDays(prev => prev?.map((day, dIdx) =>
      dIdx === dayIndex ? { ...day, meals: day?.meals?.map((meal, mIdx) => mIdx === mealIndex ? { ...meal, foods: meal?.foods?.map(f => f?.id === itemId ? { ...f, quantity: qty } : f) } : meal) } : day
    ));
  };

  const handleUpdateNutritionConfig = (field, value) => setNutritionConfig(prev => ({ ...prev, [field]: value }));

  const handleSaveNutritionPlan = async () => {
    setIsSaving(true); setErrorMsg('');
    try {
      const { data: plan, error: planError } = await supabase?.from('nutrition_plans')?.insert({
        client_id: nutritionConfig?.clientId, trainer_id: user?.id, name: nutritionConfig?.name,
        start_date: nutritionConfig?.startDate,
        end_date: nutritionConfig?.endDate || null,
        target_calories_per_day: nutritionConfig?.targetCalories || null,
        target_protein_g_per_day: nutritionConfig?.targetProtein || null,
        target_carbs_g_per_day: nutritionConfig?.targetCarbs || null,
        target_fats_g_per_day: nutritionConfig?.targetFats || null,
        is_active: true,
      })?.select()?.single();
      if (planError) throw planError;

      // Save each day that has foods
      const startDate = new Date(nutritionConfig?.startDate);
      for (let i = 0; i < mealDays?.length; i++) {
        const day = mealDays?.[i];
        const hasFoods = day?.meals?.some(m => m?.foods?.length > 0);
        if (!hasFoods) continue;

        const planDate = new Date(startDate);
        planDate?.setDate(startDate?.getDate() + i);
        const planDateStr = planDate?.toISOString()?.split('T')?.[0];

        const { data: planDay, error: dayError } = await supabase?.from('nutrition_plan_days')?.insert({
          nutrition_plan_id: plan?.id,
          plan_date: planDateStr,
          target_calories: nutritionConfig?.targetCalories || null,
          target_protein_g: nutritionConfig?.targetProtein || null,
          target_carbs_g: nutritionConfig?.targetCarbs || null,
          target_fats_g: nutritionConfig?.targetFats || null,
        })?.select()?.single();
        if (dayError) throw dayError;

        for (let mIdx = 0; mIdx < day?.meals?.length; mIdx++) {
          const meal = day?.meals?.[mIdx];
          if (!meal?.foods?.length) continue;

          const { data: mealRow, error: mealError } = await supabase?.from('nutrition_meals')?.insert({
            nutrition_plan_day_id: planDay?.id,
            name: meal?.name,
            order_index: mIdx,
          })?.select()?.single();
          if (mealError) throw mealError;

          const itemInserts = meal?.foods?.map((food, fIdx) => ({
            nutrition_meal_id: mealRow?.id,
            food_type: 'library',
            quantity: food?.quantity || 1,
            order_index: fIdx,
          }));
          if (itemInserts?.length > 0) {
            const { error: itemError } = await supabase?.from('nutrition_items')?.insert(itemInserts);
            if (itemError) throw itemError;
          }
        }
      }

      setSuccessMsg(`Nutrition plan "${nutritionConfig?.name}" saved and assigned successfully!`);
      setTimeout(() => setSuccessMsg(''), 4000);
      setNutritionConfig({ name: '', description: '', clientId: '', startDate: '', endDate: '', targetCalories: 0, targetProtein: 0, targetCarbs: 0, targetFats: 0 });
      setMealDays(initMealDays());
      loadData();
    } catch (err) {
      console.error('Save nutrition plan error:', err);
      setErrorMsg(err?.message || 'Failed to save nutrition plan. Please try again.');
      setTimeout(() => setErrorMsg(''), 5000);
    } finally { setIsSaving(false); }
  };

  const allAddedIds = weekDays?.flatMap(d => d?.exercises?.map(e => e?.exerciseId));
  const allAddedFoodIds = mealDays?.flatMap(d => d?.meals?.flatMap(m => m?.foods?.map(f => f?.foodId)));
  const dayTargets = { calories: nutritionConfig?.targetCalories || 0, protein: nutritionConfig?.targetProtein || 0, carbs: nutritionConfig?.targetCarbs || 0, fats: nutritionConfig?.targetFats || 0 };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0A0A0B' }}>
        <div className="text-center space-y-3">
          <div className="w-12 h-12 border-4 rounded-full animate-spin mx-auto" style={{ borderColor: 'rgba(0,212,255,0.2)', borderTopColor: '#00D4FF' }} />
          <p className="text-sm" style={{ color: 'rgba(255,255,255,0.45)' }}>Loading builder...</p>
        </div>
      </div>
    );
  }

  const isWorkout = mainTab === 'workout';

  return (
    <>
      <Helmet>
        <title>Nutrition Plan Builder - Form Forge</title>
      </Helmet>
      <div className="min-h-screen" style={{ background: '#0A0A0B' }}>
        <Header />
        <main className="container mx-auto px-4 py-6">

          {/* Header */}
          <div data-tour="builder-header" className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <h1
                className="text-2xl font-extrabold tracking-tight"
                style={{ color: '#fff', fontFamily: "'Inter Tight', 'Inter', sans-serif", letterSpacing: '-0.02em' }}
              >
                Nutrition Plan Builder
              </h1>
              <p className="text-sm mt-1" style={{ color: 'rgba(255,255,255,0.45)' }}>
                Design personalised nutrition plans with macro targets
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowSavedNutritionPlans(!showSavedNutritionPlans)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)' }}
              >
                <Icon name="FolderOpen" size={16} />
                Saved Plans ({savedNutritionPlans?.length})
              </button>
              <button
                onClick={() => { setMealDays(initMealDays()); setNutritionConfig({ name: '', description: '', clientId: '', startDate: '', endDate: '', targetCalories: 0, targetProtein: 0, targetCarbs: 0, targetFats: 0 }); }}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)' }}
              >
                <Icon name="RotateCcw" size={16} />
                Reset
              </button>
            </div>
          </div>

          {/* Notifications */}
          <AnimatePresence>
            {successMsg && (
              <motion.div
                initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                className="mb-4 flex items-center gap-3 p-4 rounded-xl"
                style={{ background: 'rgba(170,255,0,0.06)', border: '1px solid rgba(170,255,0,0.25)', boxShadow: '0 0 12px rgba(170,255,0,0.1)' }}
              >
                <Icon name="CheckCircle" size={18} className="flex-shrink-0" style={{ color: '#AAFF00' }} />
                <p className="text-sm font-medium" style={{ color: '#AAFF00' }}>{successMsg}</p>
              </motion.div>
            )}
            {errorMsg && (
              <motion.div
                initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                className="mb-4 flex items-center gap-3 p-4 rounded-xl"
                style={{ background: 'rgba(255,68,68,0.06)', border: '1px solid rgba(255,68,68,0.25)' }}
              >
                <Icon name="AlertCircle" size={18} className="flex-shrink-0" style={{ color: '#ff4444' }} />
                <p className="text-sm font-medium" style={{ color: '#ff4444' }}>{errorMsg}</p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Saved Plans Drawers */}
          <AnimatePresence>
            {!isWorkout && showSavedNutritionPlans && (
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden mb-6">
                <div className="rounded-xl p-4" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', backdropFilter: 'blur(20px)' }}>
                  <h3 className="text-sm font-bold mb-3" style={{ color: '#fff', fontFamily: "'Inter Tight', 'Inter', sans-serif" }}>Recent Nutrition Plans</h3>
                  {savedNutritionPlans?.length === 0 ? (
                    <p className="text-sm" style={{ color: 'rgba(255,255,255,0.4)' }}>No nutrition plans saved yet</p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {savedNutritionPlans?.map(plan => (
                        <div key={plan?.id} className="p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
                          <p className="text-sm font-semibold" style={{ color: '#fff', fontFamily: "'Inter Tight', 'Inter', sans-serif" }}>{plan?.name}</p>
                          <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.35)' }}>{plan?.client?.full_name} · {plan?.start_date ? new Date(plan?.start_date)?.toLocaleDateString() : 'No date'}</p>
                          {plan?.target_calories && <span className="text-xs mt-1 inline-block" style={{ color: '#FF6B00' }}>{plan?.target_calories} kcal/day</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Mobile Tab Navigation */}
          {isMobile && (
            <div className="flex gap-1 mb-4 p-1 rounded-xl" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
              {[{ id: 'library', label: 'Foods', icon: 'UtensilsCrossed' }, { id: 'timeline', label: 'Meals', icon: 'CalendarDays' }, { id: 'config', label: 'Settings', icon: 'Settings' }]?.map(tab => (
                <button
                  key={tab?.id}
                  onClick={() => setActivePanel(tab?.id)}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-semibold rounded-lg transition-all"
                  style={{
                    background: activePanel === tab?.id ? 'rgba(255,255,255,0.08)' : 'transparent',
                    color: activePanel === tab?.id ? '#fff' : 'rgba(255,255,255,0.4)',
                    fontFamily: "'Inter Tight', 'Inter', sans-serif",
                  }}
                >
                  <Icon name={tab?.icon} size={14} />
                  {tab?.label}
                </button>
              ))}
            </div>
          )}

          {/* Main Layout */}
          {isMobile ? (
            <div className="rounded-xl p-4 min-h-[600px]" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', backdropFilter: 'blur(20px)' }}>
              {isWorkout ? (
                <>
                  {activePanel === 'library' && <div data-tour="exercise-library"><TrainerExerciseLibraryPanel exercises={exercises} customExercises={customExercises} onAddExercise={handleAddExercise} addedExerciseIds={allAddedIds} onAddCustomExercise={handleAddCustomExercise} /></div>}
                  {activePanel === 'timeline' && <div data-tour="workout-timeline"><WeeklyProgramTimeline weekDays={weekDays} onDropToDay={handleDropToDay} onRemoveFromDay={handleRemoveFromDay} onUpdateItem={handleUpdateItem} onToggleRestDay={handleToggleRestDay} /></div>}
                  {activePanel === 'config' && <div data-tour="plan-config"><PlanConfigPanel clients={clients} planConfig={planConfig} onUpdateConfig={handleUpdateConfig} onSavePlan={handleSavePlan} isSaving={isSaving} weekDays={weekDays} saveBtnAttr={{ 'data-tour': 'save-plan-btn' }} /></div>}
                </>
              ) : (
                <>
                  {activePanel === 'library' && <NutritionFoodLibraryPanel foods={foods} customFoods={customFoods} onAddFood={handleAddFood} addedFoodIds={allAddedFoodIds} onAddCustomFood={handleAddCustomFood} />}
                  {activePanel === 'timeline' && <DailyMealPlannerTimeline mealDays={mealDays} onDropToMeal={handleDropToMeal} onRemoveFromMeal={handleRemoveFromMeal} onUpdateQty={handleUpdateQty} dayTargets={dayTargets} />}
                  {activePanel === 'config' && <NutritionPlanConfigPanel clients={clients} nutritionConfig={nutritionConfig} onUpdateConfig={handleUpdateNutritionConfig} onSavePlan={handleSaveNutritionPlan} isSaving={isSaving} mealDays={mealDays} />}
                </>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-12 gap-4">
              {/* Left Panel — Exercise Library */}
              <div
                data-tour="exercise-library"
                className="col-span-3 rounded-xl p-4 h-[calc(100vh-260px)] overflow-hidden flex flex-col"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', backdropFilter: 'blur(20px)' }}
              >
                {isWorkout
                  ? <div data-tour="exercise-search"><TrainerExerciseLibraryPanel exercises={exercises} customExercises={customExercises} onAddExercise={handleAddExercise} addedExerciseIds={allAddedIds} onAddCustomExercise={handleAddCustomExercise} /></div>
                  : <NutritionFoodLibraryPanel foods={foods} customFoods={customFoods} onAddFood={handleAddFood} addedFoodIds={allAddedFoodIds} onAddCustomFood={handleAddCustomFood} />
                }
              </div>

              {/* Center Panel — Drag-Drop Timeline */}
              <div
                data-tour="workout-timeline"
                className="col-span-6 rounded-xl p-4 h-[calc(100vh-260px)] overflow-hidden flex flex-col"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', backdropFilter: 'blur(20px)' }}
              >
                {isWorkout
                  ? <WeeklyProgramTimeline weekDays={weekDays} onDropToDay={handleDropToDay} onRemoveFromDay={handleRemoveFromDay} onUpdateItem={handleUpdateItem} onToggleRestDay={handleToggleRestDay} />
                  : <DailyMealPlannerTimeline mealDays={mealDays} onDropToMeal={handleDropToMeal} onRemoveFromMeal={handleRemoveFromMeal} onUpdateQty={handleUpdateQty} dayTargets={dayTargets} />
                }
              </div>

              {/* Right Panel — Config & Client Assignment */}
              <div
                data-tour="plan-config"
                className="col-span-3 rounded-xl p-4 h-[calc(100vh-260px)] overflow-hidden flex flex-col"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', backdropFilter: 'blur(20px)' }}
              >
                {isWorkout
                  ? <PlanConfigPanel clients={clients} planConfig={planConfig} onUpdateConfig={handleUpdateConfig} onSavePlan={handleSavePlan} isSaving={isSaving} weekDays={weekDays} saveBtnAttr={{ 'data-tour': 'save-plan-btn' }} />
                  : <NutritionPlanConfigPanel clients={clients} nutritionConfig={nutritionConfig} onUpdateConfig={handleUpdateNutritionConfig} onSavePlan={handleSaveNutritionPlan} isSaving={isSaving} mealDays={mealDays} />
                }
              </div>
            </div>
          )}
        </main>
      </div>
      {/* Workout Builder Guided Tour */}
      <WorkoutBuilderTour onComplete={() => {}} onSkip={() => {}} />
    </>
  );
};

export default TrainerWorkoutBuilder;
